// SETTINGS V2 — Digital nameplates (B7-FE1, plan §6A.2/§6A.3/§6A.4).
//
// SHAPE: a nameplate is per-machine, Settings is site-wide, so this is ONE fleet
// table with a per-machine deep link on the same route:
//   /settings/nameplates              — the table
//   /settings/nameplates/:machineId   — the SAME page; that row auto-scrolls,
//                                       highlights, and opens its detail. This is
//                                       where the machine QR-dialog cog lands, so
//                                       the user arrives on the machine they came
//                                       from instead of having to re-find it.
//
// EXACTLY THREE AFFORDANCES (owner ruling D5): the on/off switch, the ID link +
// copy, and the QR view + PNG download. No scan KPI, no document list here
// (document ticks live on the machine profile, ruling D4), no transfer flow, and
// no regenerate — see the guard note in NameplateRowDetailV2.tsx.
//
// The fleet list carries no code and no link (SEC-REQ-B7-13). Link + QR are
// fetched per machine on a user-initiated expand — one GET per expand, which is
// intended, not the render-time fan-out B7-BE4 exists to prevent.
//
// The whole /settings tree sits behind PermissionRouteGuard(Modules.Settings), so
// this panel is effectively Admin-only UI (owner ruling R1-A); the API itself
// stays Machinery:Write. Inline styles + scoped CSS-var tokens + v2 atoms. No
// MUI, no dt tokens, no Tailwind. Page ROOT carries no animation (SP2 rule).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import Loader from "../../../../shared/components/Loader";
import { translateApiError } from "../../../../shared/utils/translateApiError";
import {
  fetchNameplateFleet,
  fetchNameplateStatus,
  enableNameplate,
  disableNameplate,
  clearNameplateActionError,
} from "../../../machinery/customerNameplatesSlice";
import type { NameplateFleetItem } from "../../../machinery/models/nameplateModels";
import { SErrorCallout } from "../atoms";
import SettingsV2ConfirmDialog from "../dialogs/SettingsV2ConfirmDialog";
import NameplateEnableDialogV2 from "../dialogs/NameplateEnableDialogV2";
import NameplateRowV2 from "./NameplateRowV2";

const rowDomId = (machineId: string) => `sv2-nameplate-${machineId}`;

const NameplatesPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const { machineId: deepLinkId } = useParams<{ machineId?: string }>();

  const { fleet, fleetLoading, fleetError, statuses, statusLoading, busyMachineId, actionError } =
    useAppSelector((s) => s.customerNameplates);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [enableTarget, setEnableTarget] = useState<NameplateFleetItem | null>(null);
  const [disableTarget, setDisableTarget] = useState<NameplateFleetItem | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  // The deep link is honoured ONCE per machineId — re-collapsing the row must not
  // make the effect re-open it on the next render.
  const handledDeepLink = useRef<string | null>(null);

  useEffect(() => {
    dispatch(fetchNameplateFleet());
  }, [dispatch]);

  useEffect(
    () => () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    },
    []
  );

  const openDetail = (machineId: string) => {
    setExpandedId(machineId);
    if (!statuses[machineId] && !statusLoading[machineId]) {
      dispatch(fetchNameplateStatus(machineId));
    }
  };

  const toggleExpand = (machineId: string) => {
    if (expandedId === machineId) {
      setExpandedId(null);
      return;
    }
    openDetail(machineId);
  };

  // Deep link (/settings/nameplates/:machineId) — expand, scroll, flash, once the
  // fleet is on screen. A machineId that is not in the fleet (decommissioned, or
  // another tenant's) simply does nothing; the table stays usable.
  useEffect(() => {
    if (!deepLinkId || fleet.length === 0) return;
    if (handledDeepLink.current === deepLinkId) return;
    if (!fleet.some((x) => x.machineId === deepLinkId)) return;

    handledDeepLink.current = deepLinkId;
    openDetail(deepLinkId);
    setFlashId(deepLinkId);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashId(null), 1800);
    // Next tick — the row must be rendered before it can be scrolled to.
    window.setTimeout(() => {
      document
        .getElementById(rowDomId(deepLinkId))
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId, fleet]);

  const handleConfirmEnable = async () => {
    const target = enableTarget;
    setEnableTarget(null);
    if (!target) return;
    const r = await dispatch(enableNameplate(target.machineId));
    if (enableNameplate.fulfilled.match(r)) {
      // Open the detail so the link + QR the owner now needs are right there.
      // No fetch: the enable response IS the fresh status and the slice has
      // already cached it (openDetail would read a stale closure and refetch).
      setExpandedId(target.machineId);
    }
  };

  const handleConfirmDisable = async () => {
    const target = disableTarget;
    setDisableTarget(null);
    if (!target) return;
    await dispatch(disableNameplate(target.machineId));
  };

  const enabledCount = useMemo(() => fleet.filter((x) => x.enabled).length, [fleet]);

  const renderList = () => {
    if (fleetLoading && fleet.length === 0) {
      return (
        <div style={{ display: "grid", placeItems: "center", padding: "48px 0" }}>
          <Loader />
        </div>
      );
    }
    if (fleet.length === 0) {
      if (fleetError) return null;
      return (
        <div
          style={{
            border: "1px dashed var(--border)",
            borderRadius: 8,
            padding: "32px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>
            {t("settings:nameplates.emptyTitle")}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
            {t("settings:nameplates.emptyHint")}
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {fleet.map((item, index) => (
          <div
            key={item.machineId}
            style={{ animation: `sv2-card-reveal 0.4s ease-out ${Math.min(index, 8) * 0.05}s both` }}
          >
            <NameplateRowV2
              item={item}
              expanded={expandedId === item.machineId}
              onToggleExpand={() => toggleExpand(item.machineId)}
              onRequestEnable={setEnableTarget}
              onRequestDisable={setDisableTarget}
              busy={busyMachineId === item.machineId}
              status={statuses[item.machineId]}
              statusLoading={!!statusLoading[item.machineId]}
              domId={rowDomId(item.machineId)}
              flashing={flashId === item.machineId}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0, flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "var(--muted)", flex: 1, minWidth: 0 }}>
          {t("settings:nameplates.subtitle")}
        </div>
        {fleet.length > 0 && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--muted)",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {/* `enabled`/`total`, NOT `count` — a `count` arg would send i18next
                looking for plural suffixes this key deliberately does not have. */}
            {t("settings:nameplates.enabledCount", {
              enabled: enabledCount,
              total: fleet.length,
            })}
          </span>
        )}
      </div>

      {fleetError && (
        <SErrorCallout
          title={t("settings:nameplates.loadFailed")}
          body={translateApiError(fleetError, t, t("settings:nameplates.genericError"))}
          actionLabel={t("settings:nameplates.retry")}
          onAction={() => dispatch(fetchNameplateFleet())}
        />
      )}

      {actionError && (
        <SErrorCallout
          title={t("settings:nameplates.actionFailed")}
          body={translateApiError(actionError, t, t("settings:nameplates.genericError"))}
          actionLabel={tCommon("buttons.close")}
          onAction={() => dispatch(clearNameplateActionError())}
        />
      )}

      {renderList()}

      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          color: "var(--faint)",
          letterSpacing: "0.04em",
        }}
      >
        {t("settings:nameplates.footnote")}
      </div>

      <NameplateEnableDialogV2
        open={!!enableTarget}
        target={enableTarget}
        onClose={() => setEnableTarget(null)}
        onConfirm={handleConfirmEnable}
        busy={!!busyMachineId}
      />

      <SettingsV2ConfirmDialog
        open={!!disableTarget}
        onClose={() => setDisableTarget(null)}
        title={t("settings:nameplates.disableConfirmTitle")}
        text={t("settings:nameplates.disableConfirmBody")}
        confirmLabel={t("settings:nameplates.disableConfirmAction")}
        cancelLabel={tCommon("buttons.cancel")}
        onConfirm={handleConfirmDisable}
      />
    </div>
  );
};

export default NameplatesPageV2;
